import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { coursesService } from '../../services/api';
import type { AdminCoursePracticeSession } from '../../types';

type TaskDraft = { id?: string; text: string };

function normalizeTasks(rows: AdminCoursePracticeSession['tasks']): TaskDraft[] {
  if (!rows?.length) return [{ text: '' }];
  return rows.map((t) => ({ id: t.id, text: t.text }));
}

export default function CoursePracticeSessionsTab({ courseId }: { courseId: string }) {
  const [rows, setRows] = useState<AdminCoursePracticeSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [published, setPublished] = useState(true);
  const [pamphletFile, setPamphletFile] = useState<File | null>(null);
  const [tasks, setTasks] = useState<TaskDraft[]>([{ text: '' }]);
  const [clearPamphlet, setClearPamphlet] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await coursesService.getPracticeSessionsAdmin(courseId);
      setRows(data);
    } catch (e: unknown) {
      setError((e as Error)?.message || 'خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [rows],
  );

  const resetForm = () => {
    setCreating(false);
    setEditingId(null);
    setTitle('');
    setSortOrder('0');
    setPublished(true);
    setPamphletFile(null);
    setTasks([{ text: '' }]);
    setClearPamphlet(false);
  };

  const startEdit = (r: AdminCoursePracticeSession) => {
    setEditingId(r.id);
    setCreating(false);
    setTitle(r.title);
    setSortOrder(String(r.sortOrder ?? 0));
    setPublished(r.published);
    setPamphletFile(null);
    setClearPamphlet(false);
    setTasks(normalizeTasks(r.tasks));
  };

  const tasksPayload = (): { text: string; sortOrder: number }[] =>
    tasks
      .map((t, idx) => ({ text: t.text.trim(), sortOrder: idx }))
      .filter((t) => t.text.length > 0);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('عنوان جلسه الزامی است');
      return;
    }
    const body = tasksPayload();
    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('sortOrder', sortOrder || '0');
    fd.append('published', published ? 'true' : 'false');
    fd.append('tasksJson', JSON.stringify(body));
    if (pamphletFile) fd.append('pamphlet', pamphletFile);

    setBusyId('_new');
    setError('');
    try {
      await coursesService.createPracticeSession(courseId, fd);
      resetForm();
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      setError(typeof msg === 'string' ? msg : 'خطا در ثبت جلسه');
    } finally {
      setBusyId(null);
    }
  };

  const submitUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    const body = tasksPayload();
    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('sortOrder', sortOrder || '0');
    fd.append('published', published ? 'true' : 'false');
    fd.append('tasksJson', JSON.stringify(body));
    if (pamphletFile) fd.append('pamphlet', pamphletFile);
    if (clearPamphlet) fd.append('clearPamphlet', 'true');

    setBusyId(editingId);
    setError('');
    try {
      await coursesService.patchPracticeSession(courseId, editingId, fd);
      resetForm();
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      setError(typeof msg === 'string' ? msg : 'خطا در ذخیره');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (sessionId: string) => {
    if (!window.confirm('این جلسهٔ تمرین با چک‌لیست و جزوه حذف شود؟')) return;
    setBusyId(sessionId);
    setError('');
    try {
      await coursesService.deletePracticeSession(courseId, sessionId);
      await load();
      if (editingId === sessionId) resetForm();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5 text-right">
      <div className="rounded-xl border border-emerald-200 bg-gradient-to-bl from-emerald-50 to-white p-4">
        <p className="text-sm font-bold text-emerald-900">تمرین‌ها و جزوات جلسات (دستی)</p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
          برای هر جلسه عنوان، جزوه PDF و آیتم‌های چک‌لیست را تنظیم کنید. جزوه اختیاری است. تاریخچهٔ انجام
          چک‌لیست و بازخورد کاربران روی سرور برای خریداران دوره ذخیره می‌شود.
        </p>
      </div>

      {!creating && editingId === null && (
        <button
          type="button"
          onClick={() => {
            resetForm();
            setCreating(true);
          }}
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 text-white px-4 py-2 text-sm font-bold hover:bg-emerald-800"
        >
          + جلسهٔ تمرین جدید
        </button>
      )}

      {(creating || editingId) && (
        <form
          onSubmit={editingId ? submitUpdate : submitCreate}
          className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3 shadow-sm"
        >
          <div className="flex justify-between items-center border-b pb-2">
            <span className="text-sm font-bold text-slate-800">
              {editingId ? 'ویرایش جلسهٔ تمرین' : 'جلسهٔ تمرین جدید'}
            </span>
            <button type="button" className="text-xs text-red-600" onClick={resetForm}>
              انصراف
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">عنوان جلسه</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">ترتیب نمایش</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                id="ps-published"
                type="checkbox"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
              />
              <label htmlFor="ps-published" className="text-sm text-slate-700">
                منتشر شده (در سایت دیده شود)
              </label>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                جزوه PDF {!editingId && '(اختیاری)'}
              </label>
              <input type="file" accept="application/pdf,.pdf" onChange={(e) => setPamphletFile(e.target.files?.[0] || null)} />
              {pamphletFile && (
                <p className="text-[11px] text-slate-500 mt-1">انتخاب شده: {pamphletFile.name}</p>
              )}
              {editingId && (
                <label className="inline-flex items-center gap-2 mt-2 text-xs text-red-700">
                  <input
                    type="checkbox"
                    checked={clearPamphlet}
                    onChange={(e) => setClearPamphlet(e.target.checked)}
                  />
                  حذف جزوه قبلی بدون آپلود فایل جدید
                </label>
              )}
            </div>

            <div className="sm:col-span-2 space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-xs font-semibold text-slate-700">چک‌لیست تمرین</label>
                <button
                  type="button"
                  className="text-xs font-semibold text-[#1C3B9B]"
                  onClick={() => setTasks((prev) => [...prev, { text: '' }])}
                >
                  + آیتم
                </button>
              </div>
              {tasks.map((row, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    value={row.text}
                    onChange={(e) =>
                      setTasks((prev) => prev.map((p, i) => (i === idx ? { ...p, text: e.target.value } : p)))
                    }
                    className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    placeholder={`آیتم ${idx + 1}`}
                  />
                  <button
                    type="button"
                    disabled={tasks.length <= 1}
                    onClick={() => setTasks((prev) => prev.filter((_, i) => i !== idx))}
                    className="text-xs text-red-600 px-2 disabled:opacity-30"
                  >
                    حذف
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={busyId !== null}
            className="rounded-xl bg-[#1C3B9B] text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            {busyId !== null ? 'در حال ذخیره…' : editingId ? 'ذخیرهٔ تغییرات' : 'ثبت جلسه'}
          </button>
        </form>
      )}

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

      {loading ? (
        <p className="text-sm text-slate-500">در حال بارگذاری…</p>
      ) : (
        <div className="space-y-2">
          {sorted.map((r) => (
            <div
              key={r.id}
              className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 flex flex-wrap gap-3 justify-between items-start"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm font-bold text-slate-900">{r.title}</p>
                <p className="text-[11px] text-slate-500">
                  ترتیب {r.sortOrder} · {r.tasks.length} آیتم چک‌لیست · جزوه {r.pamphletUrl ? 'بله' : 'خیر'} · منتشر{' '}
                  {r.published ? 'بله' : 'خیر'}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  disabled={busyId !== null || creating || editingId !== null}
                  onClick={() => startEdit(r)}
                  className="text-xs font-semibold text-[#1C3B9B] px-2 py-1 border border-[#1C3B9B]/30 rounded-lg hover:bg-white"
                >
                  ویرایش
                </button>
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => handleDelete(r.id)}
                  className="text-xs font-semibold text-red-600 px-2 py-1 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  حذف
                </button>
              </div>
            </div>
          ))}
          {sorted.length === 0 && !creating && <p className="text-sm text-slate-500">هنوز جلسه‌ای برای تمرین ثبت نشده.</p>}
        </div>
      )}
    </div>
  );
}
